import { PatientDoc } from '@usacsmideo/types';
import { useAuth } from '../contexts/AuthContext';
import usePatientDoc from './usePatientDoc';

/**
 * ECN Subtask 8 (CU-86ahjxqvp) — the single source of truth for "who is this user."
 *
 * Two independent, non-mutually-exclusive persona flags live on the PatientDoc
 * (see the Persona & Sessions-Surface Convergence redesign §1):
 *   - isPatient            — the user has a patient chart.
 *   - isMIDEOFamilyMember  — the user has accepted ≥1 agent relationship.
 * A person can be a patient, an agent, or both. Capability is the union.
 *
 * Derived purely by composing the existing useAuth() + usePatientDoc() listeners
 * — NO new fetch, NO new provider. Callers gate chrome and routes on this.
 *
 * `personaResolved` is false until BOTH auth and the PatientDoc listener have
 * resolved, so guards can render a spinner instead of flashing the wrong shell
 * (or crashing on undefined) during the first-login frame race.
 *
 * IMPORTANT: persona keys off `isPatient` / doc-presence, NOT chart truthiness —
 * an empty chart subcollection still resolves to `{}` (isSuccess true), so a
 * chart-based check would misclassify an agent as a patient.
 */
export interface CurrentUserPersona {
  isPatient: boolean;
  isMIDEOFamilyMember: boolean;
  /** True once auth + the PatientDoc listener have both resolved. */
  personaResolved: boolean;
}

export function useCurrentUserPersona(): CurrentUserPersona {
  const { currentUser } = useAuth();

  const patientDocQuery = usePatientDoc(currentUser?.uid ?? '', {
    enabled: !!currentUser,
  });
  const patientDocData = patientDocQuery.data as PatientDoc | undefined;
  const { hasResolved: patientDocResolved } = patientDocQuery;

  // `currentUser` is `undefined` while auth initializes, `null` when signed out,
  // a User when signed in. Auth is "resolved" once it is not `undefined`.
  const authResolved = currentUser !== undefined;

  // Signed-out users have a resolved-but-empty persona.
  if (!currentUser) {
    return {
      isPatient: false,
      isMIDEOFamilyMember: false,
      personaResolved: authResolved,
    };
  }

  return {
    isPatient: patientDocData?.isPatient ?? false,
    isMIDEOFamilyMember: patientDocData?.isMIDEOFamilyMember ?? false,
    personaResolved: authResolved && patientDocResolved,
  };
}

export default useCurrentUserPersona;
